'use client';

import { create } from 'zustand';

import type {
  ImageAspectRatio,
  ImageModel,
  ImageOutputLimit,
  ImageResolution,
  MidjourneyQuality,
  KlingVideoModel,
  KlingVideoResolution,
  SeedanceModel,
  SeedanceResolution,
  VideoAspectRatio,
  VideoDuration,
  VideoProvider,
  VideoProviderPromptEnhance,
  VideoSubMode,
} from '@/types/chat';
import { DEFAULT_IMAGE_MODEL, DEFAULT_VIDEO_PROVIDER } from '@/types/chat';
import { requireGvOmniResolution } from '@/lib/gv-video-config';

interface ExploreGenerationSettingsState {
  imageModel: ImageModel;
  imageResolution: ImageResolution;
  imageAspectRatio: ImageAspectRatio;
  midjourneyQuality: MidjourneyQuality;
  midjourneyHd: boolean;
  midjourneyDraft: boolean;
  imageOutputLimit: ImageOutputLimit;
  videoProvider: VideoProvider;
  videoDuration: VideoDuration;
  videoSubMode: VideoSubMode;
  videoAspectRatio: VideoAspectRatio;
  klingModel: KlingVideoModel;
  klingGenerateAudio: boolean;
  providerPromptEnhance: VideoProviderPromptEnhance | null;
  klingResolution: KlingVideoResolution;
  seedanceModel: SeedanceModel;
  seedanceResolution: SeedanceResolution;
  setImageModel: (value: ImageModel) => void;
  setImageResolution: (value: ImageResolution) => void;
  setImageAspectRatio: (value: ImageAspectRatio) => void;
  setMidjourneyQuality: (value: MidjourneyQuality) => void;
  setMidjourneyHd: (value: boolean) => void;
  setMidjourneyDraft: (value: boolean) => void;
  setImageOutputLimit: (value: ImageOutputLimit) => void;
  setVideoProvider: (value: VideoProvider) => void;
  setVideoDuration: (value: VideoDuration) => void;
  setVideoSubMode: (value: VideoSubMode) => void;
  setVideoAspectRatio: (value: VideoAspectRatio) => void;
  setKlingModel: (value: KlingVideoModel) => void;
  setKlingGenerateAudio: (value: boolean) => void;
  setProviderPromptEnhance: (value: VideoProviderPromptEnhance | null) => void;
  setKlingResolution: (value: KlingVideoResolution) => void;
  setSeedanceModel: (value: SeedanceModel) => void;
  setSeedanceResolution: (value: SeedanceResolution) => void;
}

/**
 * Explore only needs generation preferences. Keeping those preferences in the
 * 7k-line canvas chat store made the discovery route compile the entire canvas
 * runtime before a user had opened a project.
 */
export const useExploreGenerationSettingsStore = create<ExploreGenerationSettingsState>((set, get) => ({
  imageModel: DEFAULT_IMAGE_MODEL,
  imageResolution: '1K',
  imageAspectRatio: 'auto',
  midjourneyQuality: 1,
  midjourneyHd: false,
  midjourneyDraft: false,
  imageOutputLimit: 1,
  videoProvider: DEFAULT_VIDEO_PROVIDER,
  videoDuration: '5',
  videoSubMode: 'universal_ref',
  videoAspectRatio: '9:16',
  klingModel: 'kling-v3',
  klingGenerateAudio: false,
  providerPromptEnhance: null,
  klingResolution: '720P',
  seedanceModel: 'seedance-2.0',
  seedanceResolution: '480P',
  setImageModel: (imageModel) => set({ imageModel }),
  setImageResolution: (imageResolution) => set({ imageResolution }),
  setImageAspectRatio: (imageAspectRatio) => set({ imageAspectRatio }),
  setMidjourneyQuality: (midjourneyQuality) => set({ midjourneyQuality }),
  setMidjourneyHd: (midjourneyHd) => set({ midjourneyHd }),
  setMidjourneyDraft: (midjourneyDraft) => set({ midjourneyDraft }),
  setImageOutputLimit: (imageOutputLimit) => set({ imageOutputLimit }),
  setVideoProvider: (videoProvider) => set({ videoProvider }),
  setVideoDuration: (videoDuration) => set({ videoDuration }),
  setVideoSubMode: (videoSubMode) => set({ videoSubMode }),
  setVideoAspectRatio: (videoAspectRatio) => set({ videoAspectRatio }),
  setKlingModel: (klingModel) => set({ klingModel }),
  setKlingGenerateAudio: (klingGenerateAudio) => set({ klingGenerateAudio }),
  setProviderPromptEnhance: (providerPromptEnhance) => set({ providerPromptEnhance }),
  setKlingResolution: (klingResolution) => {
    if (get().videoProvider === 'gv') {
      const gvResolution = requireGvOmniResolution(klingResolution);
      set({ klingResolution: gvResolution });
      return;
    }
    set({ klingResolution });
  },
  setSeedanceModel: (seedanceModel) => set({ seedanceModel }),
  setSeedanceResolution: (seedanceResolution) => set({ seedanceResolution }),
}));

export type ExploreGenerationPreferences = {
  [K in keyof ExploreGenerationSettingsState as ExploreGenerationSettingsState[K] extends (...args: never[]) => unknown ? never : K]: ExploreGenerationSettingsState[K];
};

export function getExploreGenerationPreferences(): ExploreGenerationPreferences {
  return Object.fromEntries(Object.entries(useExploreGenerationSettingsStore.getState())
    .filter(([, value]) => typeof value !== 'function')) as ExploreGenerationPreferences;
}

/** Restore only known preference fields, never actions from browser storage. */
export function restoreExploreGenerationPreferences(value: unknown): void {
  if (!value || typeof value !== 'object') return;
  const defaults = getExploreGenerationPreferences();
  const restored = Object.fromEntries(Object.entries(defaults).flatMap(([key, current]) => {
    const candidate = (value as Record<string, unknown>)[key];
    const valid = key === 'providerPromptEnhance'
      ? candidate === null || candidate === 'enabled' || candidate === 'disabled'
      : typeof candidate === typeof current && (typeof candidate !== 'number' || Number.isFinite(candidate));
    return valid ? [[key, candidate]] : [];
  }));
  useExploreGenerationSettingsStore.setState(restored);
}
